import type { LocationQuery } from 'vue-router';
import {
  composeFlaggedTitles,
  composeNonSequentialIndexer,
  normalizeIndexerText,
  type CompileTitles,
  type FieldValue,
} from '@record-fill';
import {
  buildPendingRecordTitle,
  recordTitleOf,
  type GeneratedRecordForm,
  type GeneratedRecordHistory,
} from '@/entities/GeneratedRecord';

export const RECORD_INDEXER_QUERY = 'indexer';
export const RECORD_INDEXER_MAX_LENGTH = 200;

type IndexingForm = Pick<GeneratedRecordForm, 'matrix' | 'structure' | 'titles' | 'identification' | 'record'>;
type Values = Record<string, FieldValue>;

export function readRecordIndexerQuery(query: LocationQuery): string {
  const value = query[RECORD_INDEXER_QUERY];
  return typeof value === 'string' ? value : '';
}

export function withRecordIndexerQuery(query: LocationQuery, indexer: string): LocationQuery {
  return { ...query, [RECORD_INDEXER_QUERY]: normalizeIndexerText(indexer) };
}

export function isInspectionForm(form: IndexingForm): boolean {
  return (
    form.identification !== undefined ||
    (form.record?.inspectionType ?? null) !== null ||
    form.matrix.indexing === 'OS_OF'
  );
}

export function needsTypedIndexer(form: IndexingForm): boolean {
  return form.record === undefined && form.matrix.indexing === 'USER_DEFINED';
}

// Must match the titles the server composes for the saved record, or the legacy title elements
// would change on the sheet after Salvar.
export function buildGenerationTitles(form: IndexingForm, values: Values, typedIndexer: string): CompileTitles {
  if (form.record !== undefined || isInspectionForm(form)) return form.titles;
  const indexer = generationIndexer(form, values, typedIndexer);
  return {
    ...form.titles,
    ...(indexer === '' ? {} : { indexer }),
    fillTitles: { ...form.titles.fillTitles, ...composeFlaggedTitles(form.structure, values) },
  };
}

function generationIndexer(form: IndexingForm, values: Values, typedIndexer: string): string {
  if (form.matrix.indexing === 'NON_SEQUENTIAL') return composeNonSequentialIndexer(form.structure, values);
  return needsTypedIndexer(form) ? normalizeIndexerText(typedIndexer) : '';
}

// The history carries the titles of the latest answers; an older response must show the titles its
// own answers composed, or a legacy title element reads a value the response never had.
export function titlesAtResponse(
  history: Pick<GeneratedRecordHistory, 'matrix' | 'structure' | 'titles' | 'record'>,
  values: Values
): CompileTitles {
  if (history.record.serialNumber !== null) return history.titles;
  const indexer =
    history.matrix.indexing === 'NON_SEQUENTIAL' ? composeNonSequentialIndexer(history.structure, values) : null;
  return {
    ...history.titles,
    ...(indexer === null ? {} : { indexer }),
    fillTitles: { ...history.titles.fillTitles, ...composeFlaggedTitles(history.structure, values) },
  };
}

function recordTitle(form: IndexingForm, values: Values, typedIndexer: string): string | null {
  const { matrix, record } = form;
  if (record) return recordTitleOf(record);
  if (matrix.indexing === 'NON_SEQUENTIAL') {
    return buildPendingRecordTitle(matrix.prefix, composeNonSequentialIndexer(form.structure, values));
  }
  return needsTypedIndexer(form) ? buildPendingRecordTitle(matrix.prefix, typedIndexer) : null;
}

export function buildRecordLine(form: IndexingForm, values: Values, typedIndexer: string): string | null {
  const title = recordTitle(form, values, typedIndexer);
  return title === null ? null : `Registro ${title}`;
}
